import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, VStack, HStack, Text, Portal, IconButton, Tooltip, Divider, useColorMode,
} from '@chakra-ui/react';
import { FiX, FiEyeOff, FiChevronRight } from 'react-icons/fi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDraggableFloat, computeMenuPlacement } from '../hooks/useDraggableFloat';
import { useHideToEdge } from '../hooks/useHideToEdge';
import { RESET_FLOATING_PANELS } from '../hooks/floatingPanels';
import { getColor } from '../brandConfig';
import { ModuleConfig } from '../types/ModuleConfig';
import { useAuth } from '../contexts/AuthContext';

/**
 * ModuleNavLauncher — the module's own navigation, as a draggable floating button.
 *
 * This REPLACES `ModuleBreadcrumb`, the sticky bar that used to sit under the site navbar on
 * every module page. The bar was a fixed strip of screen that every page paid for whether it
 * needed navigation or not, and on a wide table (the commission board) it ate the row the user
 * actually came to read.
 *
 * It implements `vault/how-to/draggable-floating-launchers.md` in full — every part of that
 * standard is here for a reason someone already hit:
 *
 *  - **Drag to reposition**, persisted per module per browser. A fixed button always ends up on
 *    top of somebody's primary control; letting the user move it solves that for every page they
 *    meet later, not just the one that prompted the complaint.
 *  - **`wasJustDragged()` guards the click**, so ending a drag on the button doesn't also open it.
 *  - **`computeMenuPlacement` in its own Portal at viewport coordinates**, and the returned
 *    `maxHeight` is APPLIED. A fixed `70vh` cap is not enough: with the button parked mid-screen
 *    the menu's bottom runs off the viewport, which is the exact bug that cap was believed to
 *    prevent.
 *  - **Hide-to-edge-tab.** The user can dismiss the launcher, and while hidden EVERYTHING
 *    un-renders except a translucent tab on the nearest edge. Never nothing — for the navigation
 *    launcher, rendering nothing means stranding someone with no navigation.
 *  - **The reset broadcast un-hides and snaps home, and clears its own storage.** A programmatic
 *    move never writes storage (the hook only persists on drag-end), so a listener that didn't
 *    also `removeItem` would let the lost position return on the next reload.
 *
 * Permission filtering and active-item matching are carried over verbatim from the breadcrumb it
 * replaces — this changes where navigation lives, not who can reach it.
 */

const BUTTON_SIZE = 52;

// z-index ladder (draggable-floating-launchers.md § Reset-panels broadcast).
// 998 = nav FAB + its edge tab · 999 = nav menu panel.
const Z_FAB = 998;
const Z_MENU = 999;

export const ModuleNavLauncher: React.FC<{ moduleConfig: ModuleConfig }> = ({ moduleConfig }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { colorMode } = useColorMode();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  const posKey = `og_module_nav_pos_${moduleConfig.id}`;
  const hiddenKey = `og_module_nav_hidden_${moduleConfig.id}`;

  const panelBg = colorMode === 'light' ? 'rgba(255,255,255,0.98)' : 'rgba(12,12,18,0.96)';
  const panelBorder = getColor('border.darkCard', colorMode);
  const textPrimary = getColor(colorMode === 'light' ? 'text.primary' : 'text.primaryDark', colorMode);
  const textSecondary = getColor(colorMode === 'light' ? 'text.secondary' : 'text.secondaryDark', colorMode);
  const primaryColor = getColor('primary', colorMode);

  const { position, setPosition, dragHandlers, wasJustDragged } = useDraggableFloat({
    // Per module, per browser: someone who lives in the Command Centre can park it differently
    // from where they want it in Clients.
    storageKey: posKey,
    size: BUTTON_SIZE,
    // Left side by default — the site's own FloatingNavbar owns the bottom-left corner too, so
    // this sits above it rather than on top of it.
    defaultPosition: (vw, vh) => ({ left: 20, top: vh - BUTTON_SIZE - 96 }),
  });

  const { hidden, setHidden } = useHideToEdge(hiddenKey);

  // The reset failsafe: snap home, un-hide, close — and clear our own keys, because the hook
  // only writes storage on drag-end and would otherwise resurrect the lost position on reload.
  useEffect(() => {
    const onReset = () => {
      setOpen(false);
      setPosition({ left: 20, top: Math.max(8, window.innerHeight - BUTTON_SIZE - 96) });
      try {
        localStorage.removeItem(posKey);
        localStorage.removeItem(hiddenKey);
      } catch { /* private browsing */ }
    };
    window.addEventListener(RESET_FLOATING_PANELS, onReset);
    return () => window.removeEventListener(RESET_FLOATING_PANELS, onReset);
  }, [posKey, hiddenKey, setPosition]);

  // Close on an outside click, the same way the site navbar's menu does.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const el = e.target as Node;
      if (wrapRef.current?.contains(el)) return;
      if ((el as HTMLElement)?.closest?.('.module-nav-panel')) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // A route change means they've arrived — get out of the way.
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const hasPermission = (permissions?: string[]) => {
    if (!permissions || permissions.length === 0) return true;
    if (!user?.permissions) return false;
    return permissions.some((p) =>
      user.permissions?.includes(p) ||
      user.permissions?.includes('ADMIN') ||
      user.permissions?.includes('TENANT_ADMIN'));
  };

  const items = (moduleConfig.navigation || []).filter((i) => hasPermission(i.permissions));
  if (!items.length) return null;

  // ---- Hidden: the edge tab, and nothing else. It is the guarantee that hiding can never
  // strand the user, so it is never gated behind anything. ----
  if (hidden) {
    return (
      <Portal>
        <Tooltip hasArrow placement="right" label={`Bring the ${moduleConfig.name} menu back`}>
          <Box
            as="button"
            aria-label={`Bring the ${moduleConfig.name} menu back`}
            position="fixed"
            left={0}
            top={`${Math.min(Math.max(position.top, 8), Math.max(8, (typeof window !== 'undefined' ? window.innerHeight : 800) - 52))}px`}
            width="14px"
            height="44px"
            bg={moduleConfig.color || primaryColor}
            opacity={0.45}
            _hover={{ opacity: 0.9 }}
            borderRightRadius="md"
            zIndex={Z_FAB}
            display="flex"
            alignItems="center"
            justifyContent="center"
            color="white"
            onClick={() => setHidden(false)}
          >
            <FiChevronRight size={11} />
          </Box>
        </Tooltip>
      </Portal>
    );
  }

  // Longest path first, so /command/board wins over /command for /command/board/:id.
  const activePath = [...items]
    .sort((a, b) => b.path.length - a.path.length)
    .find((i) => location.pathname === i.path || location.pathname.startsWith(`${i.path}/`))?.path;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const placement = computeMenuPlacement(
    { left: position.left, top: position.top, size: BUTTON_SIZE },
    { w: vw, h: vh },
    { preferredWidth: 300, estimatedHeight: 96 + items.length * 40, gap: 12 },
    12,
  );

  return (
    <>
      {/* The panel lives in its own Portal at viewport coordinates — anchoring it to the 52px
          button container is what used to push menus off-screen on mobile. */}
      {open && (
        <Portal>
          <Box
            className="module-nav-panel"
            position="fixed"
            {...(placement.top !== undefined ? { top: `${placement.top}px` } : {})}
            {...(placement.bottom !== undefined ? { bottom: `${placement.bottom}px` } : {})}
            left={`${placement.left}px`}
            width={`${placement.width}px`}
            zIndex={Z_MENU}
          >
            <Box
              bg={panelBg}
              backdropFilter="blur(20px)"
              borderWidth="1px"
              borderColor={panelBorder}
              borderRadius="xl"
              boxShadow="2xl"
              p={2}
              // The COMPUTED cap, not a fixed vh — a mid-viewport button leaves less room than
              // 70vh and the menu's bottom would run off the screen.
              maxH={`${placement.maxHeight}px`}
              overflowY="auto"
            >
              <HStack px={2} pt={1} pb={2} spacing={2}>
                <Text fontSize="lg">{moduleConfig.icon}</Text>
                <Text fontSize="sm" fontWeight="bold" color={textPrimary}>{moduleConfig.name}</Text>
              </HStack>
              <Divider borderColor={panelBorder} mb={1} />
              <VStack align="stretch" spacing={0}>
                {items.map((item) => {
                  const isActive = activePath === item.path;
                  return (
                    <Button
                      key={item.path}
                      size="sm"
                      variant="ghost"
                      justifyContent="flex-start"
                      fontWeight={isActive ? 'semibold' : 'normal'}
                      color={isActive ? primaryColor : textSecondary}
                      bg={isActive ? 'rgba(91, 33, 182, 0.12)' : 'transparent'}
                      leftIcon={<Text fontSize="sm">{item.icon}</Text>}
                      onClick={() => { setOpen(false); navigate(item.path); }}
                    >
                      {item.label}
                    </Button>
                  );
                })}
              </VStack>
              {/* No reset row here, deliberately: a reset reachable only by opening the launcher
                  is useless precisely when the launcher is the thing that's lost. It lives in
                  the footer instead. */}
              <Text fontSize="2xs" color={textSecondary} px={2} pt={2} opacity={0.8}>
                Drag the button to move it — it stays where you put it.
              </Text>
            </Box>
          </Box>
        </Portal>
      )}

      <Portal>
        <Box
          ref={wrapRef}
          position="fixed"
          top={`${position.top}px`}
          left={`${position.left}px`}
          zIndex={Z_FAB}
        >
          {/* Hide-to-edge: hugs the FAB, and hiding also closes the menu — never leave an open
              panel with no button to reopen it. */}
          <Tooltip
            hasArrow
            placement="top"
            label="Hide this menu — it becomes a small tab on the left edge, and “↻ Reset floating panels” in the footer brings it back"
          >
            <IconButton
              aria-label="Hide the module menu"
              icon={<FiEyeOff />}
              size="xs"
              isRound
              position="absolute"
              top="-8px"
              left="-10px"
              zIndex={1}
              bg="blackAlpha.600"
              color="white"
              _hover={{ bg: 'blackAlpha.800' }}
              onClick={() => { setOpen(false); setHidden(true); }}
            />
          </Tooltip>

          <Tooltip
            hasArrow
            placement="right"
            openDelay={500}
            label={`${moduleConfig.name} menu — drag to move, tap to open`}
          >
            <IconButton
              {...dragHandlers}
              aria-label={`${moduleConfig.name} menu — drag to move, tap to open`}
              icon={open ? <FiX /> : <Text fontSize="xl">{moduleConfig.icon}</Text>}
              isRound
              size="lg"
              boxShadow="lg"
              bg={moduleConfig.color || primaryColor}
              color="white"
              _hover={{ opacity: 0.9 }}
              onClick={() => {
                if (wasJustDragged()) return; // a drag must not also count as a tap
                setOpen((v) => !v);
              }}
            />
          </Tooltip>
        </Box>
      </Portal>
    </>
  );
};

export default ModuleNavLauncher;
